import React, { useState, useEffect, useRef, useMemo,useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ToastContainer, toast } from 'react-toastify';

import { TextField, InputAdornment } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { PuffLoader } from 'react-spinners';
import '../Document/Document.scss';


import ClientItemsService from '../../Services/clientItems.service';
import UserService from '../../Services/user.service';
import CustomDeleteConfirmationModal from '../../Components/CustomDeleteConfirmationModal/CustomDeleteConfirmationModal';
import ClientItemDraggableDialog from '../../Components/ClientItems/SubComponents/ClientItemDraggableDialog';
import ClientItemCreateModal from '../../Components/ClientItems/SubComponents/ClientItemCreateModal';
import ClientItemsExcelImportModal from '../../Components/ClientItems/SubComponents/ClientItemsExcelImportModal';
import ClientItemDataGrid from '../../Components/ClientItems/SubComponents/ClientItemDataGrid';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext';
import { NavHistoryContext } from '../../Contexts/NavHistoryContext';
import NavigationButton from '../../Components/NavigationButton/NavigationButton.jsx';
import { normalizePrices, pricesToApiDto, getDefaultPriceAmount } from '../../helpers/clientItemPrices';

/** API rows may use camelCase or PascalCase; normalize once for grid, search, edit, and delete. */
function normalizeClientItemRow(item) {
    if (!item || typeof item !== 'object') return item;
    const pricesRaw = item.prices ?? item.Prices;
    const prices = Array.isArray(pricesRaw)
        ? pricesRaw.map((p) => ({
            id: p.id ?? p.Id ?? null,
            name: p.name ?? p.Name ?? '',
            amount: p.amount ?? p.Amount ?? 0,
            isDefault: p.isDefault ?? p.IsDefault ?? false,
        }))
        : [];
    const rawPrice = item.price ?? item.Price ?? null;
    const defaultFromPrices = prices.length > 0
        ? (prices.find((p) => p.isDefault) || prices[0])?.amount
        : null;
    const price = defaultFromPrices != null && defaultFromPrices !== ''
        ? defaultFromPrices
        : rawPrice;
    return {
        ...item,
        id: item.id ?? item.Id,
        caseId: item.caseId ?? item.CaseId,
        makat: item.makat ?? item.Makat ?? '',
        name: item.name ?? item.Name ?? '',
        price,
        includeVat: item.includeVat ?? item.IncludeVat ?? false,
        imageUrl: item.imageUrl ?? item.ImageUrl ?? null,
        isDefaultItem: item.isDefaultItem ?? item.IsDefaultItem ?? false,
        prices: prices.length > 0
            ? prices
            : (price != null ? [{ id: null, name: '', amount: price, isDefault: true }] : []),
    };
}

function ClientItems() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t, i18n } = useTranslation();
    const { goBack } = useContext(NavHistoryContext);

    // Direction (RTL / LTR)
    const [direction, setDirection] = useState('rtl');
    useEffect(() => {
        // Example: if your language is 'en' => direction = 'ltr'
        if (i18n.language === 'en') {
            setDirection('ltr');
        } else {
            setDirection('rtl');
        }
    }, [i18n.language]);

    // Local states
    const [user, setUser] = useState(null);
    const [token, setToken] = useState('');
    const [client, setClient] = useState(null);

    // The main array of client items
    const [data, setData] = useState([]);

    // Searching
    const [searchQuery, setSearchQuery] = useState('');

    // Loading & Modals
    const [loading, setLoading] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);

    // We can store the item being edited in the draggable dialog
    const [editingItem, setEditingItem] = useState(null);
    const [openInlineEditModal, setOpenInlineEditModal] = useState(false);

    // Create-modal visibility
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [excelImportOpen, setExcelImportOpen] = useState(false);

    // If you allow row selection for bulk actions, you can track that here:
    const [selectedRows, setSelectedRows] = useState([]);

    const { setMenuProps } = useHamburgerMenu();
    useEffect(() => {
        setMenuProps({ client });        
        }, [client, setMenuProps]);

    // ===========================
    // 1) Load user & client data
    // ===========================
    useEffect(() => {
        (async () => {
            try {
                const currentUser = await UserService.getCurrentUser();
                const currentToken = await UserService.getCurrentToken();
                if (!currentUser || !currentToken) {
                    navigate('/login');
                    return;
                }
                setUser(currentUser);
                setToken(currentToken);

                // If the parent route passes a client or similar data, store it:
                if (location.state?.client) {
                    setClient(location.state.client);
                }

                // Then fetch the initial list of items
                await fetchClientItems(currentUser, currentToken, location.state?.client);
            } catch (err) {
                console.error('Error loading user data:', err);
                navigate('/login');
            }
        })();
    }, [location.state?.client, navigate]);

    // ===========================
    // 2) Fetch & Refresh
    // ===========================
    const fetchClientItems = async (userObj, userToken, clientObj) => {
        setLoading(true);
        try {
            if (!clientObj) return;

            // Example call to your service:
            const resp = await ClientItemsService.GetClientItemsByCaseId(
                clientObj.caseId,
                userToken
            );
            if (!resp.ok) {
                const errorText = await resp.text();
                toast.error(t('ErrorFetchingClientItems') + `: ${errorText}`);
            } else {
                const jsonData = await resp.json();
                setData(Array.isArray(jsonData) ? jsonData.map(normalizeClientItemRow) : jsonData);
            }
        } catch (err) {
            console.error(err);
            toast.error(t('FailedToFetchClientItems'));
        }
        setLoading(false);
    };

    // ===========================
    // 3) Searching & Filtering
    // ===========================
    const filteredData = useMemo(() => {
        if (!searchQuery) return data;
        const lowerQuery = searchQuery.toLowerCase();
        return data.filter((item) => {
            return (
                item.makat?.toLowerCase().includes(lowerQuery) ||
                item.name?.toLowerCase().includes(lowerQuery) ||
                item.price?.toString().includes(lowerQuery)
            );
        });
    }, [data, searchQuery]);

    // ===========================
    // 4) Deletion
    // ===========================
    const handleDeleteClick = (item) => {
        setItemToDelete(item);
        setDeleteModalVisible(true);
    };
    const handleConfirmDelete = async () => {
        if (!itemToDelete) return;
        setLoading(true);
        try {
            const makatKey = itemToDelete.makat ?? itemToDelete.Makat ?? '';
            const resp = await ClientItemsService.DeleteClientItem(
                makatKey,
                client?.caseId,
                token
            );
            if (!resp.ok) {
                const errorTxt = await resp.text();
                // Check if error is about inventory items reference
                if (errorTxt.includes('referenced by inventory items')) {
                    toast.error(t('CannotDeleteClientItemReferencedByInventory'));
                } else {
                    toast.error(t('FailedToDeleteClientItem') + `: ${errorTxt}`);
                }
            } else {
                toast.success(t('ClientItemDeletedSuccessfully'));
                // Refresh
                await fetchClientItems(user, token, client);
            }
        } catch (err) {
            console.error(err);
            toast.error(t('ErrorDeletingClientItem'));
        }
        setItemToDelete(null);
        setDeleteModalVisible(false);
        setLoading(false);
    };

    // ===========================
    // 5) Editing
    // ===========================
    const handleEdit = (item) => {
        setEditingItem(item);
        setOpenInlineEditModal(true);
    };
    const handleSaveEdit = async (updatedItem) => {
        setLoading(true);
        try {
            let imageUrl = null;

            if (updatedItem.imageFile) {
                const uploadResp = await ClientItemsService.UploadClientItemImage(
                    updatedItem.imageFile,
                    client.caseId,
                    updatedItem.makat,
                    token
                );
                if (uploadResp.ok) {
                    const uploadData = await uploadResp.json();
                    imageUrl = uploadData.imageUrl;
                } else {
                    toast.error(t('FailedToUploadImage'));
                }
            }

            const originalMakat = editingItem.makat ?? editingItem.Makat ?? '';
            const pricesDto = pricesToApiDto(updatedItem.prices);
            const defaultPrice = getDefaultPriceAmount(updatedItem.prices, updatedItem.price) || 0;
            const resp = await ClientItemsService.UpdateClientItem(
                client.caseId,
                originalMakat,
                updatedItem.makat,      // new makat
                updatedItem.name,
                defaultPrice,
                updatedItem.includeVat === true,
                imageUrl,
                token,
                false,
                pricesDto
            );
            if (!resp.ok) {
                const errorTxt = await resp.text();
                toast.error(t('FailedToUpdateClientItem') + `: ${errorTxt}`);
            } else {
                toast.success(t('ClientItemUpdatedSuccessfully'));
                await fetchClientItems(user, token, client);
            }
        } catch (error) {
            console.error(error);
            toast.error(t('ErrorUpdatingClientItem'));
        }
        setOpenInlineEditModal(false);
        setEditingItem(null);
        setLoading(false);
    };

    // Cancel editing
    const handleCloseEdit = () => {
        setOpenInlineEditModal(false);
        setEditingItem(null);
    };

    const handleImageRemoved = async (itemId) => {
        setEditingItem((prev) =>
            prev && (prev.id === itemId || prev.Id === itemId)
                ? { ...prev, imageUrl: null }
                : prev
        );
        setData((prev) =>
            prev.map((item) =>
                item.id === itemId || item.Id === itemId ? { ...item, imageUrl: null } : item
            )
        );
        await fetchClientItems(user, token, client);
    };

    // ===========================
    // 6) Creating (multiple or single)
    // ===========================
    const handleCreate = () => {
        setCreateModalVisible(true);
    };
    const handleCloseCreateModal = () => {
        setCreateModalVisible(false);
    };
    const handleSaveCreate = async (itemsToAdd) => {
        // itemsToAdd is an array of objects with { makat, name, price, includeVat, imageFile }
        setLoading(true);
        try {
            // Upload images first and get their S3 keys
            const itemsWithImageUrls = await Promise.all(
                itemsToAdd.map(async (item) => {
                    let imageUrl = null;
                    
                    // If there's an image file, upload it
                    if (item.imageFile) {
                        try {
                            const uploadResp = await ClientItemsService.UploadClientItemImage(
                                item.imageFile,
                                client.caseId,
                                item.makat,
                                token
                            );
                            if (uploadResp.ok) {
                                const uploadData = await uploadResp.json();
                                imageUrl = uploadData.imageUrl;
                            } else {
                                toast.warning(t('FailedToUploadImageForItem') + ` ${item.makat}`);
                            }
                        } catch (err) {
                            console.error('Image upload error:', err);
                        }
                    }

                    return {
                        CaseId: client.caseId,
                        Makat: item.makat,
                        Name: item.name,
                        Price: getDefaultPriceAmount(item.prices, item.price) || 0,
                        IncludeVat: item.includeVat === true,
                        ImageUrl: imageUrl,
                        Prices: pricesToApiDto(item.prices || normalizePrices(null, item.price)),
                    };
                })
            );

            const resp = await ClientItemsService.AddClientItems(itemsWithImageUrls, token);
            if (!resp.ok) {
                const errText = await resp.text();
                toast.error(t('FailedToAddClientItem') + `: ${errText}`);
            } else {
                toast.success(t('ClientItemAddedSuccessfully'));
                fetchClientItems(user, token, client);
            }
        } catch (err) {
            toast.error(t('ErrorAddingClientItem'));
            console.error(err);
        }
        setCreateModalVisible(false);
        setLoading(false);
    };

    const handleExcelImportCommit = async (items) => {
        if (!client?.caseId || !token) return false;
        setLoading(true);
        try {
            const itemsWithImageUrls = items.map((item) => ({
                CaseId: client.caseId,
                Makat: item.makat,
                Name: item.name,
                Price: parseFloat(item.price) || 0,
                IncludeVat: item.includeVat === true,
                ImageUrl: null,
            }));

            const resp = await ClientItemsService.AddClientItems(itemsWithImageUrls, token);
            if (!resp.ok) {
                const errText = await resp.text();
                toast.error(`${t('FailedToAddClientItem')}: ${errText}`);
                return false;
            }
            toast.success(t('ClientItemAddedSuccessfully'));
            await fetchClientItems(user, token, client);
            return true;
        } catch (err) {
            console.error(err);
            toast.error(t('ErrorAddingClientItem'));
            return false;
        } finally {
            setLoading(false);
        }
    };

    // ===========================
    // 7) Table Row Selections
    // ===========================
    const handleSelectRows = (rowIds) => {
        setSelectedRows(rowIds);
    };

    // ===========================
    // 8) Render
    // ===========================
    const handleGoBack = () => {

        goBack();
    };

    const fileInputRef = useRef();
    const buttonData = [
        {
            label: 'AddClientItem',
            onClick: () => handleCreate(),
        },
        {
            label: 'ClientItemsImportFromExcel',
            onClick: () => setExcelImportOpen(true),
            disabled: !client,
        },
    ];
    return (
        <>
            <>
                <>

                    {/* Actions row: create new items button, search bar, etc. */}
                    <div className={`searchFilter-bar__container ${direction}`} style={{ marginTop: '10px', alignItems: 'center' , gap: '15px'}}>
                        <TextField
                            size="small"
                            dir={direction}
                            placeholder={t('Search')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="document-search-bar"
                            variant="standard"
                            InputProps={{
                                disableUnderline: true,
                                className: `custom-input-box-sizing ${direction === 'ltr' ? 'ltr-input' : 'rtl-input'}`,
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon className="document-search-icon" />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <NavigationButton
                            t={t}
                            direction={direction}
                            fileInputRef={fileInputRef}
                            data={buttonData}
                        />
                    </div>

                    {/* DataGrid */}
                    <ClientItemDataGrid
                        data={filteredData}
                        direction={direction}
                        onEdit={handleEdit}
                        onDelete={handleDeleteClick}
                        onSelectRows={handleSelectRows}
                    />
                </>

                {/* Delete Confirmation Modal */}
                <CustomDeleteConfirmationModal
                    isVisible={deleteModalVisible}
                    onClose={() => setDeleteModalVisible(false)}
                    onDelete={handleConfirmDelete}
                />

                {/* Draggable Inline Edit Modal */}
                {editingItem && openInlineEditModal && (
                    <ClientItemDraggableDialog
                        key={editingItem.id ?? editingItem.makat ?? editingItem.Makat ?? 'edit'}
                        open={openInlineEditModal}
                        onClose={handleCloseEdit}
                        data={editingItem}
                        onSave={handleSaveEdit}
                        onImageRemoved={handleImageRemoved}
                        caseId={client?.caseId}
                        token={token}
                        direction={direction}
                        t={t}
                    />
                )}

                {/* Create Modal */}
                {createModalVisible && (
                    <ClientItemCreateModal
                        open={createModalVisible}
                        onClose={handleCloseCreateModal}
                        onSave={handleSaveCreate}
                        direction={direction}
                        t={t}
                        existingItems={data}
                    />
                )}

                <ClientItemsExcelImportModal
                    open={excelImportOpen}
                    onClose={() => setExcelImportOpen(false)}
                    direction={direction}
                    existingItems={data}
                    onCommit={handleExcelImportCommit}
                />

                <ToastContainer />
                {loading && (
                    <div className="loader-container">
                        <PuffLoader size={100} loading={loading} />
                    </div>
                )}
            </>
        </>
    );
}

export default ClientItems;
